import { Box, Card, Heading, Paragraph, Text } from '@zillow/constellation-10';

import PropertyConstants from '../../../../../constants/PropertyConstants';

import ConfirmAddress, { renderAddressConfirmationBody } from '../../../AddPropertyModal/components/ConfirmAddress';

import { useAuth } from '../../../../AuthProvider';
import ImpreciseLocationAlert from '../../../../ImpreciseLocationAlert';
import StaticMap from '../../../../StaticMap';

import type { FC } from 'react';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { UserProps } from '../../../../../../srcLegacy/shared/model/User';

type Props = {
  address: string;
  isRoomForRent: boolean;
  numUnits?: string;
  propertyType: string;
  unit?: string;
  user?: RentalManagerAuthCheckUserInfoWebApiDto | UserProps;
  showImpreciseLocationAlert: boolean;
  questionNumber: number;
  questionTotal: number;
};

const {
  PROPERTY_TYPES: { COMMUNITY, MULTIPLEX },
} = PropertyConstants;

export const PropertyConfirmation: FC<Props> = ({
  address,
  isRoomForRent,
  numUnits,
  propertyType,
  unit,
  user: userProp,
  showImpreciseLocationAlert,
  questionNumber,
  questionTotal,
}) => {
  const isMultiplexType = propertyType === MULTIPLEX;
  const authContext = useAuth?.();
  const user = authContext?.user ?? undefined;

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '500',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Card
        elevated={false}
        tone="soft"
        css={{
          overflow: 'hidden',
          width: '100%',
          maxWidth: '480px',
          padding: '0',
          '& img': {
            display: 'block',
            width: '100%',
            height: 'auto',
          },
        }}
      >
        <StaticMap
          address={address}
          showImpreciseLocationAlert={false}
          isMultiplexType={isMultiplexType}
          user={user}
          size="480x240"
        />
      </Card>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
        <Text textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
          Question {questionNumber} of {questionTotal}
        </Text>
        <Heading level={2} textStyle="heading-xs">
          Is this information complete and correct?
        </Heading>
        <Paragraph>{renderAddressConfirmationBody(propertyType)}</Paragraph>
      </Box>
      <Box css={{ width: '100%' }}>
        <ConfirmAddress
          address={address}
          isRoomForRent={isRoomForRent}
          numUnits={numUnits}
          propertyType={propertyType}
          unit={unit}
          shouldShowStaticMap={false}
          showImpreciseLocationAlert={false}
          showTermsInComponent={true}
          user={user}
          embedded={true}
        />
      </Box>

      {user && showImpreciseLocationAlert && (
        <ImpreciseLocationAlert confirmAddressButtonTitle="Confirm" user={user} isOverlay={false} />
      )}
    </Box>
  );
};
